// Recurring Billing editor for the Billing Review detail modal.
// One slot per frequency (MONTHLY, QUARTERLY). View mode renders inline; edit
// and add open a popup modal with the full form. Ported from the
// swa-botfieldBilling editor; edits are SUBMITTED for Billing Team approval
// (staged flow, see billingChangeApi.js) — nothing is written live.
//
// Row shape supplied by the grid (api/shared/billingConfig/overlay.js recurringView):
//   { uuid, billType, level, population, frequency, narrative, billAmount,
//     narratives: [{ lineOrder, narrative, amount }], lumpDisbursements,
//     lumpWithParent, applicableThrough: 'YYYY-MM-DD' }
// Submit payload (PascalCase): { uuid, BillType, Level, Frequency, Population,
//   Narratives, BillAmount, LumpDisbursements, LumpWithParent, ApplicableThrough }.
//
// - N narratives per bill: BillAmount is the TOTAL; with 2+ lines each line carries
//   its own amount and the lines must sum to the total (single line = whole total).
// - "Lump disbursements" toggle (storage only).
// - "Lump BG children WIP" toggle, editable only on a billing-group PARENT
//   (client.billingGroup.isParent). When on, the parent's recurring absorbs the
//   group's children's WIP, so those children can't have their own recurring of
//   that frequency (client.recurringParentLumps = { MONTHLY, QUARTERLY }).
// - Applicable Through date + "Expired" bubble (isApplicableThroughExpired).

import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import Modal from '../../components/Modal';
import { useAuth } from '../../auth/AuthContext';
import { submitChange, withdrawChange, wasWithdrawn, withPending, withdrawnView } from './billingChangeApi';

const BILL_TYPES  = ['PROGRESS', 'INTERIM'];
const LEVELS      = ['JOB', 'SERV'];

function fmt$(n) {
  if (n == null) return '—';
  return '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Whole-cents integer for exact money comparison (avoids float drift).
const toCents = (n) => Math.round((Number(n) || 0) * 100);

// Pull narrative lines off an existing slot, tolerating both the narratives[]
// shape and the legacy single narrative/billAmount.
function narrativesFromExisting(existing) {
  if (existing && Array.isArray(existing.narratives) && existing.narratives.length) {
    return existing.narratives
      .slice()
      .sort((a, b) => (a.lineOrder ?? 0) - (b.lineOrder ?? 0))
      .map(n => ({
        narrative: n.narrative == null ? '' : String(n.narrative),
        amount: n.amount == null ? '' : String(n.amount),
      }));
  }
  const narrative = existing?.narrative ?? existing?.Narrative ?? '';
  const amount = existing?.billAmount ?? existing?.BillAmount ?? '';
  return [{ narrative: String(narrative || ''), amount: amount === '' || amount == null ? '' : String(amount) }];
}

// Applicable Through defaults to 12/31 of the current year. Past this date the
// recurring bill is no longer generated.
const defaultApplicableThrough = () => `${new Date().getFullYear()}-12-31`;
const toDateInput = (v) => {
  if (!v) return '';
  const s = String(v);
  return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : '';
};
// Display a YYYY-MM-DD date as M/D/YYYY (or "—" when unset).
const fmtThroughDate = (v) => {
  const d = toDateInput(v);
  if (!d) return '—';
  const [y, m, day] = d.split('-');
  return `${Number(m)}/${Number(day)}/${y}`;
};
// True once the current date is PAST the applicable-through date (expired).
export function isApplicableThroughExpired(dateStr) {
  const d = toDateInput(dateStr);
  if (!d) return false;
  // Compare date-only (local) so "today == through date" is still valid.
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const [y, m, day] = d.split('-').map(Number);
  const through = new Date(y, m - 1, day); through.setHours(0, 0, 0, 0);
  return today > through;
}

const newUuid = () => ((typeof crypto !== 'undefined' && crypto.randomUUID)
  ? crypto.randomUUID()
  : `${Date.now()}-${Math.random().toString(16).slice(2)}`).toLowerCase();

function emptyForm(contIndex, frequency) {
  return {
    uuid: '',
    ContIndex: contIndex,
    BillType: 'PROGRESS',
    Level: 'SERV',
    Population: [],
    Frequency: frequency,
    BillAmount: '',
    Narratives: [{ narrative: '', amount: '' }],
    LumpDisbursements: false,
    LumpWithParent: false,
    ApplicableThrough: defaultApplicableThrough(),
  };
}

// Accepts the formatted shape (lowercase) or raw (capitalized) and returns
// a form-ready object with server casing.
function toForm(existing, contIndex, frequency) {
  if (!existing) return emptyForm(contIndex, frequency);
  return {
    uuid:       existing.uuid || '',
    ContIndex:  contIndex,
    BillType:   existing.billType  || existing.BillType  || 'PROGRESS',
    Level:      existing.level     || existing.Level     || 'SERV',
    Population: existing.population || existing.Population || [],
    Frequency:  frequency,
    BillAmount: existing.billAmount != null ? String(existing.billAmount) : (existing.BillAmount != null ? String(existing.BillAmount) : ''),
    Narratives: narrativesFromExisting(existing),
    LumpDisbursements: Boolean(existing.lumpDisbursements ?? existing.LumpDisbursements),
    LumpWithParent:    Boolean(existing.lumpWithParent ?? existing.LumpWithParent),
    ApplicableThrough: toDateInput(existing.applicableThrough ?? existing.ApplicableThrough) || defaultApplicableThrough(),
  };
}

// ── Population picker ────────────────────────────────────────────────────────

function PopulationPicker({ level, value, onChange, availableServices, jobMapping }) {
  const [filter, setFilter] = useState('');

  const options = useMemo(() => {
    const rawOpts = level === 'SERV'
      ? (availableServices || []).map(code => ({ key: code, label: code, meta: '' }))
      : (jobMapping || []).map(j => ({ key: j.idx, label: j.name, meta: j.serv }));
    // Dedupe by visible (label, meta). Source JSON has been observed with
    // duplicate rows that differ only by invisible characters (NBSP,
    // zero-width joiners, Unicode width variants), so normalize aggressively:
    //   * NFKC to canonicalize compatibility forms
    //   * replace all Unicode whitespace/zero-width characters with regular
    //     spaces, then collapse runs of whitespace
    //   * trim + lowercase
    // Whitespace + NBSP + en/em-quad family + zero-width joiners + BOM + CJK space.
    const WHITESPACE_RE = /[\s\u00A0\u1680\u180E\u2000-\u200F\u202F\u205F\u2060\u3000\uFEFF]+/g;
    const norm = v => String(v ?? '').normalize('NFKC').replace(WHITESPACE_RE, ' ').trim().toLowerCase();
    const seen = new Set();
    const opts = [];
    for (const o of rawOpts) {
      const dedupeKey = `${norm(o.label)}|${norm(o.meta)}`;
      if (seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);
      opts.push(o);
    }
    // Sort by label (JobName for JOB, service code for SERV), then by meta
    // (Serv for JOB) so same-JobName rows cluster in a predictable Serv order.
    return opts.sort((a, b) => {
      const byLabel = String(a.label || '').localeCompare(
        String(b.label || ''), undefined, { sensitivity: 'base' }
      );
      if (byLabel !== 0) return byLabel;
      return String(a.meta || '').localeCompare(
        String(b.meta || ''), undefined, { sensitivity: 'base' }
      );
    });
  }, [level, availableServices, jobMapping]);

  const needle = filter.toLowerCase();
  const filtered = options.filter(o =>
    !needle ||
    String(o.label).toLowerCase().includes(needle) ||
    String(o.meta || '').toLowerCase().includes(needle) ||
    String(o.key).toLowerCase().includes(needle)
  );

  const selected = new Set((value || []).map(String));

  function toggle(key) {
    const k = String(key);
    const next = new Set(selected);
    if (next.has(k)) next.delete(k); else next.add(k);
    // Preserve original type: numbers for JOB, strings for SERV.
    const arr = [...next].map(v => level === 'JOB' ? Number(v) : v);
    onChange(arr);
  }

  return (
    <div className="rbe-pop">
      <div className="rbe-pop-chips">
        {(value || []).length === 0 ? (
          <span className="rbe-pop-empty">No {level === 'JOB' ? 'jobs' : 'services'} selected</span>
        ) : (
          (value || []).map(v => {
            const opt = options.find(o => String(o.key) === String(v));
            // For JOB level, prefix with Serv (opt.meta) instead of Idx.
            const label = opt
              ? (level === 'JOB'
                  ? `${opt.meta || '—'} — ${opt.label}`
                  : opt.label)
              : String(v);
            return (
              <span key={String(v)} className="rbe-chip">
                {label}
                <button type="button" className="rbe-chip-x" onClick={() => toggle(v)}>×</button>
              </span>
            );
          })
        )}
      </div>
      <input
        type="text"
        className="cs-input rbe-pop-filter"
        placeholder={`Search ${level === 'JOB' ? 'jobs' : 'services'}…`}
        value={filter}
        onChange={e => setFilter(e.target.value)}
      />
      <div className="rbe-pop-list">
        {filtered.length === 0 ? (
          <div className="rbe-pop-empty">No matches</div>
        ) : filtered.slice(0, 50).map(o => (
          // Idx is not unique in jobMapping.json — compose a key from
          // idx+label+meta so React treats each rendered row as distinct.
          <label key={`${o.key}|${o.label}|${o.meta}`} className="rbe-pop-item">
            <input
              type="checkbox"
              checked={selected.has(String(o.key))}
              onChange={() => toggle(o.key)}
            />{' '}
            <span className="rbe-pop-label">{o.label}</span>
            {o.meta && <span className="rbe-pop-meta">{o.meta}</span>}
          </label>
        ))}
        {filtered.length > 50 && (
          <div className="rbe-pop-empty">Showing first 50 of {filtered.length}. Type to narrow.</div>
        )}
      </div>
    </div>
  );
}

// ── One frequency slot (Monthly or Quarterly) ────────────────────────────────

function FrequencySlot({
  client, period, frequency, existing, readOnly, isGroupParent, parentLumpsThisFreq,
  pending, availableServices, jobMapping, onOptimisticUpdate, onChanged,
}) {
  const { email, isBillingTeam } = useAuth();
  // The server lets only the requester (or the Billing Team / an admin) withdraw a pending change.
  const canWithdrawPending = !!pending && (!!isBillingTeam
    || (!!email && String(pending.requestedBy || '').trim().toLowerCase() === email.trim().toLowerCase()));
  const slotKey = frequency === 'MONTHLY' ? 'recurringMonthly' : 'recurringQuarterly';
  const [modalOpen, setModalOpen] = useState(false);
  const [mode, setMode]     = useState('edit');   // 'edit' | 'add'
  const [form, setForm]     = useState(toForm(existing, client.contIndex, frequency));
  const [saving, setSaving] = useState(false);
  const [throughError, setThroughError] = useState('');   // inline error under "Applicable Through"

  function startEdit() {
    setForm(toForm(existing, client.contIndex, frequency));
    setMode('edit');
    setThroughError('');
    setModalOpen(true);
  }

  function startAdd() {
    const f = emptyForm(client.contIndex, frequency);
    f.uuid = newUuid();
    setForm(f);
    setMode('add');
    setThroughError('');
    setModalOpen(true);
  }

  function closeModal() {
    if (saving) return; // don't close mid-save
    setModalOpen(false);
  }

  // ── Narrative line helpers ──
  function addNarrative() {
    setForm(f => ({ ...f, Narratives: [...f.Narratives, { narrative: '', amount: '' }] }));
  }
  function removeNarrative(idx) {
    setForm(f => {
      if (f.Narratives.length <= 1) return f; // min 1
      return { ...f, Narratives: f.Narratives.filter((_, i) => i !== idx) };
    });
  }
  function updateNarrative(idx, patch) {
    setForm(f => ({
      ...f,
      Narratives: f.Narratives.map((n, i) => (i === idx ? { ...n, ...patch } : n)),
    }));
  }

  // Running total of narrative amounts vs. the bill total. With a single line the
  // whole total is allocated to it (no per-line Amount / reconciliation); from the
  // 2nd line on, amounts appear and must sum to the total before saving.
  const multi = form.Narratives.length > 1;
  const narrativeSum = useMemo(
    () => form.Narratives.reduce((acc, n) => acc + (Number(n.amount) || 0), 0),
    [form.Narratives]
  );
  const billTotal = Number(form.BillAmount);
  const balanced = multi
    ? (Number.isFinite(billTotal) && toCents(narrativeSum) === toCents(billTotal))
    : (Number.isFinite(billTotal) && billTotal >= 0);
  const diff = Number.isFinite(billTotal) ? narrativeSum - billTotal : NaN;

  async function save() {
    // A child whose billing parent lumps this frequency's WIP cannot create its
    // own recurring of that frequency. (Existing ones are left editable.)
    if (mode === 'add' && parentLumpsThisFreq) {
      toast.error(`This client's billing parent lumps the group's ${frequency.toLowerCase()} WIP — children can't have their own ${frequency.toLowerCase()} recurring.`);
      return;
    }
    const amt = Number(form.BillAmount);
    if (!Number.isFinite(amt) || amt < 0) { toast.error('Bill Amount must be ≥ 0.'); return; }
    if (!form.Population.length) { toast.error('Population cannot be empty.'); return; }
    if (!form.Narratives.length) { toast.error('At least one narrative line is required.'); return; }
    for (const n of form.Narratives) {
      if (!n.narrative.trim()) { toast.error('Every narrative line needs text.'); return; }
      if (multi && !Number.isFinite(Number(n.amount))) { toast.error('Every narrative line needs a numeric amount.'); return; }
    }
    if (!balanced) {
      toast.error('Narrative amounts must equal the bill amount before saving.');
      return;
    }
    // The server turns a missing date into 12/31 of this year — never let that happen unseen. An
    // end date is required: the user has to pick one.
    if (!/^\d{4}-\d{2}-\d{2}$/.test(form.ApplicableThrough || '')) {
      setThroughError('Enter an Applicable Through date — the last date this recurring bill should be created.');
      return;
    }

    // Single line carries the full bill total; multi-line lines carry their own
    // allocated amounts (validated to sum to the total above).
    const narratives = form.Narratives.map((n, i) => ({
      lineOrder: i,
      narrative: n.narrative.trim(),
      amount: multi ? Number(n.amount) : amt,
    }));
    const through = form.ApplicableThrough;
    const lumpWithParent = !!form.LumpWithParent && isGroupParent;

    const payload = {
      uuid:       String(form.uuid).toLowerCase(),
      BillType:   form.BillType,
      Level:      form.Level,
      Frequency:  frequency,
      Population: form.Population,
      Narratives: narratives,
      BillAmount: amt,
      LumpDisbursements: !!form.LumpDisbursements,
      LumpWithParent:    lumpWithParent,
      ApplicableThrough: through,
    };

    // Optimistic: show the submitted bill and its "Pending approval" tag at once and close the form;
    // a refusal restores the row and reopens the form with everything still filled in.
    const snapshotSlot = client[slotKey];
    const snapshotPending = client.pending || [];
    const body = { section: 'recurring', op: 'set', payload };
    onOptimisticUpdate?.(c => withPending({
      ...c,
      [slotKey]: {
        uuid:       payload.uuid,
        billType:   form.BillType,
        level:      form.Level,
        population: form.Population,
        frequency,
        narrative:  narratives[0]?.narrative || '',
        billAmount: amt,
        narratives,
        lumpDisbursements: payload.LumpDisbursements,
        lumpWithParent,
        applicableThrough: through,
      },
    }, { ...body, summary: `${existing ? 'Edit' : 'Add'} ${frequency.toLowerCase()} recurring bill: $${amt.toLocaleString('en-US')}`, email }));
    setModalOpen(false);
    setSaving(true);
    try {
      await submitChange({ period, contIndex: client.contIndex, ...body });
      toast.success('Submitted for Billing Team approval');
      onChanged?.();
    } catch (err) {
      onOptimisticUpdate?.(c => ({ ...c, [slotKey]: snapshotSlot, pending: snapshotPending }));
      setModalOpen(true);
      toast.error(`Submit failed: ${err.message}`, { autoClose: 10000 });
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!existing?.uuid) return;
    const snapshotSlot = client[slotKey];
    const snapshotPending = client.pending || [];
    const uuid = String(existing.uuid).toLowerCase();

    // A bill that only exists as an open request: deleting it is a withdraw (no confirm, nothing to approve).
    if (pending && canWithdrawPending && pending.changeId != null && pending.op === 'set' && !pending.before) {
      onOptimisticUpdate?.(c => withdrawnView(c, pending) || c);
      setSaving(true);
      try {
        await withdrawChange({ period, changeId: pending.changeId });
        toast.success('Recurring bill withdrawn');
        onChanged?.();
      } catch (err) {
        onOptimisticUpdate?.(c => ({ ...c, [slotKey]: snapshotSlot, pending: snapshotPending }));
        toast.error(`Withdraw failed: ${err.message}`, { autoClose: 10000 });
      } finally {
        setSaving(false);
      }
      return;
    }

    if (!window.confirm(`Submit a request to delete the ${frequency.toLowerCase()} recurring for ${client.clientCode}?`)) return;

    const body = { section: 'recurring', op: 'delete', payload: { uuid } };
    onOptimisticUpdate?.(c => withPending({ ...c, [slotKey]: null },
      { ...body, summary: `Delete ${frequency.toLowerCase()} recurring bill`, email }));
    setSaving(true);
    try {
      const out = await submitChange({ period, contIndex: client.contIndex, ...body });
      toast.success(wasWithdrawn(out) ? 'Recurring bill withdrawn' : 'Delete submitted for Billing Team approval');
      onChanged?.();
    } catch (err) {
      onOptimisticUpdate?.(c => ({ ...c, [slotKey]: snapshotSlot, pending: snapshotPending }));
      toast.error(`Delete failed: ${err.message}`, { autoClose: 10000 });
    } finally {
      setSaving(false);
    }
  }

  async function withdraw() {
    if (!pending?.changeId) return;
    // Optimistic where the old bill can be rebuilt locally (an add); otherwise the refetch fixes the row.
    const snapshotRow = client;
    const view = withdrawnView(client, pending);
    if (view) onOptimisticUpdate?.(() => view);
    setSaving(true);
    try {
      await withdrawChange({ period, changeId: pending.changeId });
      toast.success('Change withdrawn');
      onChanged?.();
    } catch (err) {
      if (view) onOptimisticUpdate?.(() => snapshotRow);
      toast.error(`Withdraw failed: ${err.message}`, { autoClose: 10000 });
    } finally {
      setSaving(false);
    }
  }

  const label = frequency.charAt(0) + frequency.slice(1).toLowerCase(); // Monthly / Quarterly
  const existingNarrCount = existing
    ? (Array.isArray(existing.narratives) && existing.narratives.length
        ? existing.narratives.length
        : 1)
    : 0;
  const addBlockedTitle = parentLumpsThisFreq
    ? `The billing parent lumps this group's ${label.toLowerCase()} WIP — children can't have their own ${label.toLowerCase()} recurring.`
    : (pending ? 'A change is already pending approval for this recurring — withdraw it first.' : undefined);

  return (
    <>
      <div className="rbe-slot">
        {/* Floats on the slot's top-left corner so it takes no room in the layout. */}
        {pending && (
          <span className="rbe-pending-float">
            <span className="br-pending-tag" title={pending.summary || 'Pending approval'}>
              <i className="fas fa-hourglass-half" /> Pending approval
            </span>
            {/* Once the manager/partner has signed off (readOnly) their submissions are final. */}
            {!readOnly && pending.changeId != null && canWithdrawPending && (
              <button type="button" className="br-pending-withdraw" onClick={withdraw} disabled={saving}>
                Withdraw
              </button>
            )}
          </span>
        )}
        <div className="rbe-slot-head">
          <h5>{label}</h5>
          {!readOnly && (existing ? (
            <div className="rbe-slot-actions">
              <button className="cs-btn" onClick={startEdit} disabled={saving}>Edit</button>
              <button className="cs-btn cs-btn--danger" onClick={remove} disabled={saving}>Delete</button>
            </div>
          ) : (
            <button
              className="cs-btn cs-btn--add"
              onClick={startAdd}
              disabled={saving || parentLumpsThisFreq || !!pending}
              title={addBlockedTitle}
            >
              <i className="fas fa-plus" /> Add {label}
            </button>
          ))}
        </div>
        {existing ? (
          <>
            <div className="detail-row"><span className="detail-label">Total Amount:</span> {fmt$(existing.billAmount ?? existing.BillAmount)}</div>
            <div className="detail-row"><span className="detail-label">Bill Type:</span> {existing.billType || existing.BillType}</div>
            <div className="detail-row"><span className="detail-label">Level:</span> {existing.level || existing.Level}</div>
            <div className="detail-row"><span className="detail-label">Population:</span> {(existing.population || existing.Population || []).length} item(s)</div>
            <div className="detail-row"><span className="detail-label">Narrative lines:</span> {existingNarrCount}</div>
            {(existing.lumpDisbursements ?? existing.LumpDisbursements) && (
              <div className="detail-row"><span className="detail-label">Lump disbursements:</span> Yes</div>
            )}
            {(existing.lumpWithParent ?? existing.LumpWithParent) && (
              <div className="detail-row"><span className="detail-label">Lump BG children WIP:</span> Yes</div>
            )}
            {(() => {
              const raw = existing.applicableThrough ?? existing.ApplicableThrough;
              const expired = isApplicableThroughExpired(raw);
              return (
                <div className="detail-row">
                  <span className="detail-label">App. Thru:</span>{' '}
                  <span className="rbe-through-val">
                    {fmtThroughDate(raw)}
                    {expired && (
                      <span
                        className="rbe-expired-bubble"
                        title="Past the applicable-through date — this recurring bill will no longer be created."
                      >
                        Expired
                      </span>
                    )}
                  </span>
                </div>
              );
            })()}
          </>
        ) : (
          <div className="detail-empty">
            {pending ? `${pending.summary || 'Change pending approval'}` : `No ${label.toLowerCase()} recurring configured`}
          </div>
        )}
      </div>

      <Modal
        open={modalOpen}
        onClose={closeModal}
        title={`${mode === 'add' ? 'New' : 'Edit'} ${label} Recurring — ${client.clientCode}`}
        wide
        wider
        footer={
          <>
            <button className="cs-btn" disabled={saving} onClick={closeModal}>Cancel</button>
            <button className="cs-btn cs-btn--save" disabled={saving || !balanced} onClick={save}>
              {saving ? 'Submitting…' : 'Submit for approval'}
            </button>
          </>
        }
      >
        <div className="rbe-form">
          <div className="rbe-form-row">
            <label>
              Bill Type
              <select
                className="cs-input"
                value={form.BillType}
                onChange={e => setForm({ ...form, BillType: e.target.value })}
                disabled={saving}
              >
                {BILL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
            <label>
              Level
              <select
                className="cs-input"
                value={form.Level}
                onChange={e => setForm({ ...form, Level: e.target.value, Population: [] })}
                disabled={saving}
              >
                {LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </label>
            <label>
              Total Bill Amount
              <input
                type="number"
                min="0" step="0.01"
                className="cs-input"
                value={form.BillAmount}
                onChange={e => setForm({ ...form, BillAmount: e.target.value })}
                disabled={saving}
              />
            </label>
            <label>
              Applicable Through
              <input
                type="date"
                className={`cs-input${throughError ? ' rbe-input--error' : ''}`}
                value={form.ApplicableThrough || ''}
                onChange={e => { setForm({ ...form, ApplicableThrough: e.target.value }); setThroughError(''); }}
                disabled={saving}
                aria-invalid={!!throughError}
                title="After this date the recurring bill will no longer be created."
              />
              {throughError && <span className="rbe-field-error" role="alert">{throughError}</span>}
            </label>

            {/* Lump toggles, to the right of the Bill Type / Level / Amount boxes. */}
            <div className="rbe-lump-toggles rbe-lump-toggles--inline">
              <label className="tech-fee-toggle">
                <input
                  type="checkbox"
                  checked={!!form.LumpDisbursements}
                  onChange={e => setForm({ ...form, LumpDisbursements: e.target.checked })}
                  disabled={saving}
                />
                <span className="tech-fee-slider" />
                <span className="tech-fee-label">Lump disbursements into total</span>
              </label>
              <label
                className={`tech-fee-toggle ${!isGroupParent ? 'rbe-lump-toggle--disabled' : ''}`}
                title={isGroupParent
                  ? `When on, this billing group's children's WIP is lumped into the parent's recurring bill, and children can't have their own ${frequency.toLowerCase()} recurring.`
                  : 'Only available for billing group parents.'}
              >
                <input
                  type="checkbox"
                  checked={!!form.LumpWithParent && isGroupParent}
                  onChange={e => setForm({ ...form, LumpWithParent: e.target.checked })}
                  disabled={saving || !isGroupParent}
                />
                <span className="tech-fee-slider" />
                <span className="tech-fee-label">Lump BG children WIP</span>
              </label>
            </div>
          </div>

          <label className="rbe-full-row">
            Population ({form.Level})
            <PopulationPicker
              level={form.Level}
              value={form.Population}
              onChange={pop => setForm({ ...form, Population: pop })}
              availableServices={availableServices}
              jobMapping={jobMapping}
            />
          </label>

          {/* N narrative lines, each with its own amount once there are 2+ */}
          <div className="rbe-full-row">
            <div className="rbe-narr-head">
              <span>Narratives</span>
              <button
                type="button"
                className="cs-btn cs-btn--add"
                onClick={addNarrative}
                disabled={saving}
              >
                <i className="fas fa-plus" /> Add Narrative
              </button>
            </div>

            {form.Narratives.map((n, i) => (
              <div key={i} className="rbe-narr-line">
                <textarea
                  className="cs-input rbe-narrative"
                  rows={2}
                  placeholder={`Narrative line ${i + 1}`}
                  value={n.narrative}
                  onChange={e => updateNarrative(i, { narrative: e.target.value })}
                  disabled={saving}
                />
                {multi && (
                  <div className="rbe-narr-amt">
                    <label className="rbe-narr-amt-label">Amount</label>
                    <input
                      type="number"
                      min="0" step="0.01"
                      className="cs-input"
                      value={n.amount}
                      onChange={e => updateNarrative(i, { amount: e.target.value })}
                      disabled={saving}
                    />
                  </div>
                )}
                <button
                  type="button"
                  className="cs-btn cs-btn--danger rbe-narr-remove"
                  onClick={() => removeNarrative(i)}
                  disabled={saving || form.Narratives.length <= 1}
                  title={form.Narratives.length <= 1 ? 'At least one narrative line is required' : 'Remove this line'}
                >
                  <i className="fas fa-trash" />
                </button>
              </div>
            ))}

            {/* Live running total vs. bill amount (only once amounts are split). */}
            {multi && (
              <div className={`rbe-narr-total ${balanced ? 'rbe-narr-total--ok' : 'rbe-narr-total--bad'}`}>
                <span>
                  Lines total: <strong>{fmt$(narrativeSum)}</strong>
                  {' '}/ Bill amount: <strong>{Number.isFinite(billTotal) ? fmt$(billTotal) : '—'}</strong>
                </span>
                {Number.isFinite(diff) && (
                  balanced
                    ? <span className="rbe-narr-badge rbe-narr-badge--ok">Balanced</span>
                    : <span className="rbe-narr-badge rbe-narr-badge--bad">
                        {diff > 0 ? `Over by ${fmt$(diff)}` : `Under by ${fmt$(Math.abs(diff))}`}
                      </span>
                )}
              </div>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
}

// ── Pending-change matching ──────────────────────────────────────────────────
// client.pending lists staged changes. A recurring change's fieldKey is
// 'recurring:<uuid lowercase>'. The overlay shows pending adds/edits as live, so
// those match by uuid; a pending DELETE leaves the slot empty, so it can only be
// matched by section (disambiguated by the frequency word in its summary).

function pendingBySlot(client) {
  const all = (client.pending || []).filter(p => p.section === 'recurring');
  const out = { MONTHLY: null, QUARTERLY: null };
  if (!all.length) return out;
  const slots = { MONTHLY: client.recurringMonthly, QUARTERLY: client.recurringQuarterly };
  const used = new Set();
  for (const f of ['MONTHLY', 'QUARTERLY']) {
    const ex = slots[f];
    if (!ex?.uuid) continue;
    const key = `recurring:${String(ex.uuid).toLowerCase()}`;
    const hit = all.find(p => p.fieldKey === key);
    if (hit) { out[f] = hit; used.add(hit); }
  }
  const knownKeys = new Set(['MONTHLY', 'QUARTERLY']
    .filter(f => slots[f]?.uuid)
    .map(f => `recurring:${String(slots[f].uuid).toLowerCase()}`));
  const unmatched = all.filter(p => !used.has(p) && !knownKeys.has(p.fieldKey));
  for (const f of ['MONTHLY', 'QUARTERLY']) {
    if (out[f] || slots[f]) continue;
    const other = f === 'MONTHLY' ? 'quarterly' : 'monthly';
    const mine = f.toLowerCase();
    const pick = unmatched.find(p => !used.has(p) && String(p.summary || '').toLowerCase().includes(mine))
      || unmatched.find(p => !used.has(p) && !String(p.summary || '').toLowerCase().includes(other));
    if (pick) { out[f] = pick; used.add(pick); }
  }
  return out;
}

// ── Parent: two slots ────────────────────────────────────────────────────────

export default function RecurringBillingEditor({
  client, period, readOnly, availableServices, jobMapping, onOptimisticUpdate, onChanged,
}) {
  // "Lump BG children WIP" is editable only on a billing-group PARENT.
  const isGroupParent = !!client.billingGroup?.isParent;
  // When this client's billing parent lumps the group's WIP for a frequency, the
  // client can't create its own recurring of that frequency (server-supplied).
  const lumps = client.recurringParentLumps || { MONTHLY: false, QUARTERLY: false };
  const pend = pendingBySlot(client);

  return (
    <div className="rbe-editor">
      <FrequencySlot
        client={client}
        period={period}
        frequency="MONTHLY"
        existing={client.recurringMonthly}
        readOnly={readOnly}
        isGroupParent={isGroupParent}
        parentLumpsThisFreq={!!lumps.MONTHLY}
        pending={pend.MONTHLY}
        availableServices={availableServices}
        jobMapping={jobMapping}
        onOptimisticUpdate={onOptimisticUpdate}
        onChanged={onChanged}
      />
      <FrequencySlot
        client={client}
        period={period}
        frequency="QUARTERLY"
        existing={client.recurringQuarterly}
        readOnly={readOnly}
        isGroupParent={isGroupParent}
        parentLumpsThisFreq={!!lumps.QUARTERLY}
        pending={pend.QUARTERLY}
        availableServices={availableServices}
        jobMapping={jobMapping}
        onOptimisticUpdate={onOptimisticUpdate}
        onChanged={onChanged}
      />
    </div>
  );
}
